import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUserRole } from '@/hooks/useUserRole'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import {
  getPolicyDeclarationFiles,
  reviewResidentDocuments,
  submitReimbursementForLibertyReview,
  submitReimbursementSection,
  unlockReimbursementSection,
} from '@/features/reimbursements/services/reimbursementsService'
import {
  appendReimbursement3ScalarFields,
  appendReimbursement3ScalarFieldsToFormData,
} from '@/features/reimbursements/utils/buildReimbursementExtendedPayload'
import { buildReimbursementUpdatePayload } from '@/features/reimbursements/utils/detailSubmitPayload'
import type { ReimbursementsDetailValues } from '@/features/reimbursements/types/schemas'
import { resolveReimbursementRentalFieldsForApi } from '../utils/detailPayloadScope'
import { MILESTONE_TO_SECTION } from '@/features/reimbursements/utils/reimbursementSectionStatus'
import {
  EMPTY_REIMBURSEMENT_EXTENDED_FIELDS,
  type ReimbursementExtendedFields,
  type ReimbursementMilestoneId,
} from '@/features/reimbursements/types/reimbursementFormTypes'
import { buildDocumentReviewPayload, buildDocumentReclassifyPayload } from '@/features/reimbursements/utils/reimbursementDocumentReviewCatalog'
import type { ReimbursementDocumentType } from '@/features/reimbursements/types/reimbursementFormTypes'
import { openServeFileInNewTabFromGesture as openServeFileInNewTab } from '@/lib/utils/serveFileHelpers'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { showApiError, showSuccess, showValidationMessage } from '@/lib/utils/notifications'
import {
  RESIDENT_COMPANY_ROLES,
  RESIDENT_LIBERTY_ROLES,
  RESIDENT_PROPERTY_ROLES,
} from '../constants/residentConstants'
import { useResidentDetail } from '../hooks/useResidentDetail'
import { useResidentDocuments } from '../hooks/useResidentDocuments'
import { usePublicNotesAndMessages } from '../hooks/usePublicNotesAndMessages'
import { useResidentNotes } from '../hooks/useResidentNotes'
import { useResidentPayments } from '../hooks/useResidentPayments'
import { useLiveReimbursementEndDatePatch } from '../hooks/useLiveReimbursementEndDatePatch'
import { formatApplicantIdSegment } from '../utils/applicantIdFormat'
import type {
  ResidentDetailTab,
  ResidentDetailConfirmState,
  PaymentReviewFormRow,
  ResidentDefaultFormValues,
  ReviewApprovalField,
  ReviewApprovalValue,
} from '../types'
import {
  deriveLeaseEndFromStart,
  deriveTimelyAndDates,
  shouldSyncDerivedAdjustedNotice,
  getInitialRentalProgress,
  getMissingDefaultRequiredFields,
  getVisibleResidentTabs,
  isReimbursementRequestDateAfterLeaseEnd,
  isResidentFormDisabled,
  canShowAdjustPaymentButton,
  sumPaymentHistoryTotalPaid,
  sumPaymentHistoryPreviousDefaultPayment,
  deriveEditPaymentInitialAmount,
  REIMBURSEMENT_REQUEST_DATE_AFTER_LEASE_END_MESSAGE,
  shouldShowSaveBar,
} from '../utils/residentValidation'
import { buildRentalListFromStatusList } from '../utils/residentListConstants'
import { isReimbursementClaimType } from '../utils/residentHeaderStatus'
import {
  apiPageForDetailTab,
  getDefaultApiPage,
  getReimbursementId,
  resolveNotesRef,
  resolveOldStatementUploadId,
} from '../utils/residentDecoupling'
import type { ResidentDetailPageType } from '../utils/residentDecoupling'
import {
  applyUnusedSecurityDepositToApiPayload,
  appendUnusedSecurityDepositToFormData,
  buildAdjustmentDataFromForm,
  buildResidentImmediateFileUploadFormData,
  buildResidentSaveFormData,
  buildResidentSavePayload,
  buildSettlementGeneratePayload,
  getCalculationTabSnapshot,
  isLeaseChanged,
  residentSaveHasFiles,
  type ResidentImmediateUploadField,
} from '../utils/residentPayload'
import {
  downloadResidentClaimsDocument,
  downloadResidentClaimsPreview,
  openApiPath,
  openResidentClaimsDocument,
  openResidentClaimsPreview,
  buildLibertyDocumentImagePreviewList,
} from '../utils/residentFiles'
import { applyLibertyTagging, type LibertyTaggingUser } from '../utils/residentTagging'
import {
  claimDenyReopen,
  fetchLibertyTaggingUsers,
  generateSettlementStatement,
  reviewResidentStatus,
  saveNote,
  saveResidentDefault,
  uploadOldStatement,
} from '../services/residentApi'
import type { EntityEditTabItem } from '@/components/ui/EntityEditTabBar'
import {
  internalNotesMessageSendTo,
  isInternalNotesOnlySave,
  type InternalNotesNotifySelection,
} from '../utils/internalNotesSubmit'
import type { PolicyDeclarationFile } from '@/features/reimbursements/types'
import {
  residentDefaultSchema,
  L1_REVIEW_ALREADY_EXISTS_API_ERROR,
  L1_REVIEW_ALREADY_EXISTS_MESSAGE,
  L1_REVIEW_RECOMMENDATION_TITLE,
  L1_REVIEW_RECOMMENDATION_MESSAGE,
} from '../constants/detailPage'
import {
  buildPublicNotesSavePayload,
  type PublicNotesNotifySelection,
} from '../utils/publicNotesSubmit'

export function useResidentDetailWorkflow(
  applicantId?: string,
  userId?: string,
  pageType: ResidentDetailPageType = 'resident-detail',
) {
  const role = useUserRole()
  const [selectedTab, setSelectedTab] = useState<ResidentDetailTab>('residentinfo')
  const [showRequired, setShowRequired] = useState(false)
  const [confirm, setConfirm] = useState<ResidentDetailConfirmState>(null)
  const [dialogs, setDialogs] = useState<Record<string, boolean>>({})
  const [policyFilesPopup, setPolicyFilesPopup] = useState<
    PolicyDeclarationFile[] | null
  >(null)
  const [accessConfirmationLoading, setAccessConfirmationLoading] = useState(false)
  const [showEorpModal, setShowEorpModal] = useState(false)
  /** Angular `isFinalLedger` one-shot flag when uploading payment ledger after Final Ledger? dialog. */
  const [ledgerUploadFinalIntent, setLedgerUploadFinalIntent] = useState<boolean | null>(null)
  const [documentUploadingField, setDocumentUploadingField] = useState<string | null>(null)
  /** Claim review-log row awaiting a rejection reason (Angular `rejectClaim` -> ReasonComponent). */
  const [pendingRejectClaimId, setPendingRejectClaimId] = useState<string | number | null>(null)
  /** Angular `claim_override` — set when a Liberty user confirms a First-Month-out-of-lease override. */
  const claimOverrideRef = useRef(false)
  const reimbursementExtendedFieldsRef = useRef<ReimbursementExtendedFields>(
    EMPTY_REIMBURSEMENT_EXTENDED_FIELDS,
  )
  /** Payment row being edited (Angular `editPayment` -> DefaultPaymentComponent in edit mode). */
  const [editPaymentRow, setEditPaymentRow] = useState<PaymentReviewFormRow | null>(null)
  /** Approval decision awaiting a reason + "Update Status" confirm (Angular approvedDefault*). */
  const [pendingApproval, setPendingApproval] = useState<
    { kind: 'payment' | 'claim'; id: string | number; field: ReviewApprovalField; value: ReviewApprovalValue } | null
  >(null)
  /** Claim L2/L3 "Approve" (value 1) opens the ReviewClaim modal (Angular ReviewClaimComponent). */
  const [approvalReviewClaim, setApprovalReviewClaim] = useState<
    {
      reviewLogId: string | number
      approvalType: string
      approvalTypeValue: string
      defaultType?: string | null
    } | null
  >(null)
  /** Bumped to remount the review-log selects so a cancelled approval reverts the dropdown. */
  const [approvalResetKey, setApprovalResetKey] = useState(0)
  const [pendingDocumentRemove, setPendingDocumentRemove] = useState<null | {
    kind: 'default' | 'liberty' | 'statement'
    id: string | number
  }>(null)
  const [statementUploading, setStatementUploading] = useState(false)

  const detail = useResidentDetail(applicantId, userId, pageType)
  const defaultId = getReimbursementId(detail.residentDetail as Record<string, unknown> | null)
  const notesRef = resolveNotesRef(
    detail.residentDetail as Record<string, unknown> | null,
    pageType,
  )
  const applicationId = detail.applicationData?.application_id
  const notesHook = useResidentNotes(
    notesRef.refId,
    detail.residentDetail?.insurance_message,
    notesRef.notesFor,
  )
  const {
    mergedRows: publicNotesMergedRows,
    loading: publicNotesLoading,
    reload: reloadPublicNotes,
  } = usePublicNotesAndMessages(notesRef.refId, detail.residentDetail, notesRef.notesFor)
  const [publicMessageSaving, setPublicMessageSaving] = useState(false)
  const [internalNoteSaving, setInternalNoteSaving] = useState(false)
  // Keep the Liberty Internal draft above the tab content so it survives tab switches.
  const [internalNotesDraft, setInternalNotesDraft] = useState('')
  const [publicNotesNotifyOpen, setPublicNotesNotifyOpen] = useState(false)
  /** Post-action refresh — silent so ResidentDetailPage does not unmount the form for a spinner. */
  const reloadDetail = useCallback(async () => {
    await Promise.all([
      detail.load({ silent: true }),
      notesHook.loadNotes({ silent: true }),
      Promise.resolve(reloadPublicNotes()),
    ])
  }, [detail, notesHook, reloadPublicNotes])
  const isLibertyRole = RESIDENT_LIBERTY_ROLES.includes(role.roleName)
  const documentsApiPage = pageType === 'resident-detail' ? 'resident' : 'default'
  // Angular `loadResidentDetail`: `loadDocuments()` runs only for liberty roles.
  const documentsHook = useResidentDocuments(
    reloadDetail,
    defaultId,
    isLibertyRole,
    documentsApiPage,
  )
  const paymentsHook = useResidentPayments(defaultId, reloadDetail, applicationId)
  const isCompanyRole = RESIDENT_COMPANY_ROLES.includes(role.roleName)
  const isPropertyRole = RESIDENT_PROPERTY_ROLES.includes(role.roleName)
  const isExternalInsurance = role.roleName === 'EXTERNAL_INSURANCE'

  const currentUserDisplayName = useMemo(() => {
    const u = role.user
    if (u?.first_name || u?.last_name) {
      return `${u.first_name || ''} ${u.last_name || ''}`.trim()
    }
    const cr = role.currentRole
    return String(cr?.role_name || 'System')
  }, [role.currentRole, role.user])

  /** Current logged-in user id — Angular `loggedUserID`, used to gate internal-note edit. */
  const loggedUserId = role.user?.user_id

  // Angular `liberty_loadTaggingUser`: active Liberty users for internal-note @mention tagging.
  const libertyTaggingUsersRef = useRef<LibertyTaggingUser[]>([])
  useEffect(() => {
    if (!isLibertyRole) return
    let cancelled = false
    void fetchLibertyTaggingUsers()
      .then((users) => {
        if (!cancelled) libertyTaggingUsersRef.current = users
      })
      .catch(() => {
        /* tagging is best-effort; a failed load just leaves notes untagged */
      })
    return () => {
      cancelled = true
    }
  }, [isLibertyRole])

  const form = useForm<ResidentDefaultFormValues>({
    resolver: zodResolver(residentDefaultSchema) as unknown as Resolver<ResidentDefaultFormValues>,
    values: detail.formValues,
    // Focus-reload (after pre-filled Skip Affidavit submit) must refresh documents/status
    // without wiping unsaved edits — especially EVICTION `skip_claim_affidavit_required === '1'`,
    // which controls whether the new doc shows under Skip Affidavit Form vs Eviction File.
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
  })
  const values = form.watch()

  const { isOverrideCleared, markEndDateOverridePreserved } = useLiveReimbursementEndDatePatch({
    form,
    detail: detail.residentDetail,
    applicationData: detail.applicationData,
  })

  useEffect(() => {
    if (window.location.hash) {
      const hash = window.location.hash.replace('#', '')
      if (hash === 'default' && pageType === 'default-detail') {
        setSelectedTab('default')
      } else if (hash === 'notification') {
        setSelectedTab('libertyinternal')
      }
      const cleanUrl = `${window.location.pathname}${window.location.search}`
      window.history.replaceState(window.history.state, '', cleanUrl)
    }
  }, [pageType])

  // Angular `loadResidentDetail`: internal notes load only for liberty roles.
  useEffect(() => {
    if (notesRef.refId && isLibertyRole) {
      void notesHook.loadNotes()
      const id = window.setInterval(() => {
        void notesHook.loadNotes({ silent: true })
      }, 30_000)
      return () => window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when notes ref changes
  }, [notesRef.refId, notesRef.notesFor, isLibertyRole])

  // Refresh resident detail when returning to tab (e.g. after submitting pre-filled affidavit in external window/tab).
  useEffect(() => {
    const onFocus = () => {
      void reloadDetail()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [reloadDetail])

  // Auto-jump when skip/eviction claim type appears (Angular `checkdefaultstatus`).
  useEffect(() => {
    const original = detail.residentDetail?.resident_default?.reimbursement_type
    const current = values.reimbursement_type
    const hadType = isReimbursementClaimType(original)
    const hasType = isReimbursementClaimType(current)
    if (pageType === 'default-detail' && hasType && !hadType && selectedTab !== 'default') {
      setShowRequired(true)
      setSelectedTab('default')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watching reimbursement_type only
  }, [values.reimbursement_type])

  // Angular `(change)="rentalStatusChange()"`: auto-pick the first available
  // rental_progress every time the user picks a status.
  const handleRentalStatusUserChange = useCallback(
    (nextStatus: string) => {
      if (!nextStatus) return
      const rentalListForProgress = detail.statusList
        ? buildRentalListFromStatusList(detail.statusList)
        : detail.rentalList
      const next = getInitialRentalProgress(nextStatus, role.roleName, rentalListForProgress)
      if (next) {
        form.setValue('rental_progress', next, { shouldDirty: true, shouldValidate: true })
      }
    },
    [detail.rentalList, detail.statusList, form, role.roleName],
  )

  // Mirror Angular's `updateContract`: when lease_start_date changes, set
  // lease_end_date to start + 1 year - 1 day and mark lease as verified.
  useEffect(() => {
    if (!form.formState.dirtyFields.lease_start_date) return
    const derived = deriveLeaseEndFromStart(values.lease_start_date)
    if (derived.lease_end_date && derived.lease_end_date !== values.lease_end_date) {
      form.setValue('lease_end_date', derived.lease_end_date, { shouldDirty: true })
    }
    if (derived.lease_verified && values.lease_verified !== derived.lease_verified) {
      form.setValue('lease_verified', derived.lease_verified, { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watching lease_start_date only
  }, [values.lease_start_date])

  // Mirror Angular's `updateLeaseEnd`: lease_end_date changed → mark verified.
  useEffect(() => {
    if (!form.formState.dirtyFields.lease_end_date) return
    if (values.lease_end_date && values.lease_verified !== '1') {
      form.setValue('lease_verified', '1', { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watching lease_end_date only
  }, [values.lease_end_date])

  // Mirror Angular's `validateFirstMonth`: derive `timely`, `adjusted_notice`
  // and `claim_expiration_date` when rental_status_date / first month change.
  // Preserve saved/manual Start Date on hydrate (CS-1148 / Angular setResidentData).
  useEffect(() => {
    const dirtyFields = form.formState.dirtyFields
    const dirty =
      Boolean(dirtyFields.rental_status_date) ||
      Boolean(dirtyFields.first_month_not_paid_month) ||
      Boolean(dirtyFields.first_month_not_paid_year)

    if (
      !shouldSyncDerivedAdjustedNotice({
        requestOrFirstMonthDirty: dirty,
        currentAdjustedNotice: values.adjusted_notice,
      })
    ) {
      return
    }

    const derived = deriveTimelyAndDates({
      rental_status_date: values.rental_status_date,
      first_month_not_paid_month: values.first_month_not_paid_month,
      first_month_not_paid_year: values.first_month_not_paid_year,
      lease_start_date: values.lease_start_date,
      isLibertyRole,
    })
    if (
      derived.first_month_not_paid_year &&
      derived.first_month_not_paid_year !== String(values.first_month_not_paid_year ?? '')
    ) {
      form.setValue('first_month_not_paid_year', derived.first_month_not_paid_year, {
        shouldDirty: dirty,
      })
    }
    if (derived.timely && derived.timely !== values.timely) {
      form.setValue('timely', derived.timely, { shouldDirty: dirty })
    }
    if (derived.adjusted_notice && derived.adjusted_notice !== values.adjusted_notice) {
      form.setValue('adjusted_notice', derived.adjusted_notice, { shouldDirty: dirty })
    }
    if (
      derived.claim_expiration_date &&
      derived.claim_expiration_date !== values.claim_expiration_date
    ) {
      form.setValue('claim_expiration_date', derived.claim_expiration_date, {
        shouldDirty: dirty,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only watch the inputs
  }, [
    values.rental_status_date,
    values.first_month_not_paid_month,
    values.first_month_not_paid_year,
    values.lease_start_date,
    values.adjusted_notice,
    isLibertyRole,
  ])

  // Mirror Angular's CLOSED -> EVICTION/SKIPPED guard. When the resident's
  // rental_status was previously CLOSED, the user may not flip it back to
  // EVICTION or SKIPPED — the lease/policy is finalised. Show a confirmation
  // dialog and revert if cancelled.
  useEffect(() => {
    const original = detail.residentDetail?.resident_default?.rental_status
    const current = values.rental_status
    if (
      original === 'CLOSED' &&
      current &&
      ['EVICTION', 'SKIPPED'].includes(current) &&
      current !== original
    ) {
      setConfirm({
        title: 'Lease / Policy Ended',
        message:
          'The lease for this resident is already closed and the policy has ended. Reimbursement cannot be filed for a closed lease.',
        primaryLabel: 'OK',
        secondaryLabel: 'Cancel',
        onPrimary: () => {
          setConfirm(null)
          form.setValue('rental_status', 'CLOSED', { shouldDirty: false })
        },
        onSecondary: () => {
          setConfirm(null)
          form.setValue('rental_status', 'CLOSED', { shouldDirty: false })
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watching rental_status only
  }, [values.rental_status])

  // Mirror Angular `updateDuplicateValue`: ONLY company roles cannot set move-in or new
  // resident lease start more than 3 weeks in the future. Property roles are NOT gated.
  useEffect(() => {
    if (!isCompanyRole) return
    const threeWeeks = 21 * 24 * 60 * 60 * 1000
    const now = Date.now()
    const checkField = (field: 'movein_date' | 'new_resident_lease_start_date') => {
      if (!form.formState.dirtyFields[field]) return
      const value = values[field]
      if (!value) return
      const parsed = new Date(value).getTime()
      if (Number.isNaN(parsed)) return
      if (parsed - now > threeWeeks) {
        showValidationMessage(
          field === 'movein_date'
            ? 'New Resident Move-In cannot exceed 3 weeks from current date'
            : 'New Resident Lease Start Date cannot exceed 3 weeks from current date',
        )
        form.setValue(field, '', { shouldDirty: true })
      }
    }
    checkField('movein_date')
    checkField('new_resident_lease_start_date')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watch only the inputs
  }, [values.movein_date, values.new_resident_lease_start_date])



  const missingFields = useMemo(
    () => (showRequired ? getMissingDefaultRequiredFields(values, role.roleName, selectedTab) : []),
    [role.roleName, selectedTab, showRequired, values],
  )

  const visibleTabs = useMemo<EntityEditTabItem[]>(
    () =>
      getVisibleResidentTabs({
        roleName: role.roleName,
        residentDetail: detail.residentDetail,
        hasPolicy: role.hasPolicy,
        pageType,
      }).map((tab) => ({
        id: tab.id,
        label: tab.label,
        testId: `resident-detail-tab-${tab.id}`,
      })),
    [detail.residentDetail, pageType, role],
  )

  // If the active tab disappears (e.g. rental_status changed from SKIPPED to ACTIVE
  // and the Reimbursement tab is no longer shown) fall back to Resident Info.
  useEffect(() => {
    if (detail.saving || detail.loading) return
    if (visibleTabs.length === 0) return
    if (!visibleTabs.some((tab) => tab.id === selectedTab)) {
      setSelectedTab(visibleTabs[0].id as ResidentDetailTab)
    }
  }, [detail.loading, detail.saving, visibleTabs, selectedTab])

  const wasSavingRef = useRef(false)
  useEffect(() => {
    if (wasSavingRef.current && !detail.saving) {
      // File uploads (and other partial saves) must not wipe an unsaved EVICTION
      // "Skip Affidavit required? = Yes" — otherwise the doc shows under Eviction File.
      const localSkip = String(form.getValues('skip_claim_affidavit_required') ?? '')
      const skipDirty = Boolean(form.formState.dirtyFields.skip_claim_affidavit_required)
      form.reset(detail.formValues, { keepDirtyValues: true })
      if (
        skipDirty &&
        (localSkip === '0' || localSkip === '1') &&
        String(detail.formValues.skip_claim_affidavit_required ?? '') !== localSkip
      ) {
        form.setValue('skip_claim_affidavit_required', localSkip, { shouldDirty: true })
      }
    }
    wasSavingRef.current = detail.saving
  }, [detail.formValues, detail.saving, form])

  useEffect(() => {
    if (selectedTab !== 'default') setLedgerUploadFinalIntent(null)
  }, [selectedTab])

  const formDisabled = isResidentFormDisabled(
    role.roleName,
    detail.residentDetail?.resident_default,
    detail.applicationData?.progress_status,
    selectedTab,
  )

  const submitPublicNotesMessage = useCallback(
    async (msg: string, selection: PublicNotesNotifySelection) => {
      if (!userId) return
      setPublicNotesNotifyOpen(false)
      setPublicMessageSaving(true)
      try {
        const page = apiPageForDetailTab(selectedTab)
        const payload = buildPublicNotesSavePayload(
          msg,
          applicationId,
          role.roleName,
          selection,
          page,
        )
        const response = (await saveResidentDefault(userId, payload)) as Record<string, unknown>
        form.setValue('message', '')

        const invalidUsers = Array.isArray(response?.invalid_user)
          ? (response.invalid_user as unknown[])
          : Array.isArray((response?.data as Record<string, unknown>)?.invalid_user)
            ? ((response.data as Record<string, unknown>).invalid_user as unknown[])
            : []

        if (invalidUsers.length > 0) {
          showValidationMessage(
            'Warning : The mail has been sent to a Property where ALL user(s) have been inactive for more than 90 days.<br>Please check the Communications History for details.',
            'Email sent to inactive user(s)',
          )
        } else {
          showSuccess(
            selection.email_sendto === 'none'
              ? 'Data Record Updated Successfully'
              : 'Message Sent Successfully',
          )
        }
        await reloadPublicNotes({ silent: true })
        // Angular `sendMessage` → `loadResidentDetail()` so `email_send_to` comes from GET.
        if (selection.email_sendto !== 'none') {
          await detail.load({ silent: true })
        }
      } catch (err) {
        showApiError(err)
      } finally {
        setPublicMessageSaving(false)
      }
    },
    [applicationId, detail, form, reloadPublicNotes, role.roleName, selectedTab, userId],
  )

  const submitInternalNote = useCallback(
    async (content: string, selection: InternalNotesNotifySelection) => {
      if (!notesRef.refId || !userId) return
      setInternalNoteSaving(true)
      try {
        const emailSendTo = selection.email_sendto
        // Angular: for Liberty roles, `@mentions` are converted to `[userid:X]` before save.
        const taggedContent = isLibertyRole
          ? applyLibertyTagging(content, libertyTaggingUsersRef.current)
          : content
        if (isInternalNotesOnlySave(emailSendTo)) {
          await saveNote(notesRef.refId, {
            notes: taggedContent,
            source: 'INTERNAL',
            notes_for: notesRef.notesFor,
            ...(emailSendTo === 'l1_review' ? { review: '1' } : {}),
          })
          showSuccess('Note Added Successfully!')
        } else {
          const messageSendTo = internalNotesMessageSendTo(emailSendTo)
          if (!messageSendTo) return
          await saveResidentDefault(userId, {
            message_insurance: taggedContent,
            message_send_to: messageSendTo,
            page: apiPageForDetailTab(selectedTab),
            application_id: applicationId,
          })
          showSuccess('Data Updated Successfully')
        }
        setInternalNotesDraft('')
        await notesHook.loadNotes({ silent: true })
        await detail.load({ silent: true })
      } catch (err: unknown) {
        // Interceptor rejects `{ message, status, data }` (not Axios `response.data`).
        if (extractErrorMessage(err) === L1_REVIEW_ALREADY_EXISTS_API_ERROR) {
          setConfirm({
            title: L1_REVIEW_RECOMMENDATION_TITLE,
            message: L1_REVIEW_ALREADY_EXISTS_MESSAGE,
            primaryLabel: 'Ok',
            secondaryLabel: '',
            messageAlign: 'left',
            messageWeight: 'normal',
            onPrimary: () => setConfirm(null),
          })
        } else {
          showApiError(err)
        }
        throw err
      } finally {
        setInternalNoteSaving(false)
      }
    },
    [applicationId, detail, isLibertyRole, notesHook, notesRef.notesFor, notesRef.refId, selectedTab, userId],
  )

  const handleSavePublicMessage = useCallback(() => {
    const msg = String(form.getValues('message') ?? '').trim()
    if (!msg || !userId) return

    if (!isLibertyRole) {
      setConfirm({
        title: 'Notes & Messages',
        message: 'Do you want to send the message?',
        primaryLabel: 'Yes',
        secondaryLabel: 'No',
        onPrimary: () => {
          setConfirm(null)
          void submitPublicNotesMessage(msg, { email_sendto: 'default_manager' })
        },
      })
      return
    }

    setPublicNotesNotifyOpen(true)
  }, [form, isLibertyRole, submitPublicNotesMessage, userId])

  const persistSave = useCallback(
    async (submitValues: ResidentDefaultFormValues, moveOutDate?: string) => {
      try {
        const leaseChanged = isLeaseChanged(submitValues, detail.residentDetail?.resident_default)
        const original = detail.residentDetail?.resident_default
        const hasFiles = residentSaveHasFiles(submitValues)
        const payload = buildResidentSavePayload({
          values: submitValues,
          applicationId: detail.applicationData?.application_id,
          roleName: role.roleName,
          leaseChanged,
          moveOutDate,
          rentalProgressChangedToEarlyEnd:
            submitValues.rental_progress === 'EARLY_END' &&
            submitValues.rental_progress !== original?.rental_progress,
          ledgerUploadFinalIntent,
          hasFileUpload: hasFiles,
          claimLock: Boolean((original as Record<string, unknown> | undefined)?.claim_lock),
          claimOverride: claimOverrideRef.current,
          pageType,
          selectedTab,
          securityDepositCollected:
            reimbursementExtendedFieldsRef.current.security_deposit_collected,
        })
        const body = hasFiles
          ? buildResidentSaveFormData(submitValues, payload)
          : payload
        if (
          apiPageForDetailTab(selectedTab) === 'default' &&
          !hasFiles &&
          body !== null &&
          typeof body === 'object' &&
          !(body instanceof FormData)
        ) {
          appendReimbursement3ScalarFields(
            body as Record<string, unknown>,
            reimbursementExtendedFieldsRef.current,
          )
        }
        await detail.save(body)
        setLedgerUploadFinalIntent(null)
        showSuccess(hasFiles ? 'File Uploaded Successfully' : 'Data Updated Successfully')
        await reloadPublicNotes()
      } catch (err) {
        showApiError(err)
      }
    },
    [
      detail,
      reloadPublicNotes,
      role.roleName,
      ledgerUploadFinalIntent,
      selectedTab,
      userId,
      pageType,
    ],
  )

  const openDialog = useCallback(
    (key: string) => setDialogs((prev) => ({ ...prev, [key]: true })),
    [],
  )
  const closeDialog = useCallback(
    (key: string) => setDialogs((prev) => ({ ...prev, [key]: false })),
    [],
  )

  /**
   * Angular `isFormValid()` + toast `'Required Fields are Missing'` (Validation Message modal).
   * Applies on **Reimbursement** tab only for SKIPPED/EVICTION: Reason, COVID-19, first month not paid month/year.
   * Used by Save, Review Reimbursement dialog, Deny/Reopen — not Cancel Reimbursement.
   */
  const notifyIfMissingRequiredReimbursementFields = useCallback((): boolean => {
    const missing = getMissingDefaultRequiredFields(form.getValues(), role.roleName, selectedTab)
    if (!missing.length) return true
    setShowRequired(true)
    showValidationMessage('Required Fields are Missing')
    return false
  }, [form, role.roleName, selectedTab])

  const handleRentalStatusDateUserChange = useCallback(
    (rentalStatusDate: string) => {
      const contractEnd = detail.applicationData?.contract_end_date
      if (!contractEnd || !rentalStatusDate) return
      if (
        !isReimbursementRequestDateAfterLeaseEnd(
          values.reimbursement_type,
          rentalStatusDate,
          String(contractEnd),
        )
      ) {
        return
      }
      showValidationMessage(REIMBURSEMENT_REQUEST_DATE_AFTER_LEASE_END_MESSAGE)
      form.setValue('rental_status_date', '', { shouldDirty: true })
    },
    [detail.applicationData?.contract_end_date, form, values.reimbursement_type],
  )

  const save = form.handleSubmit(async (submitValues) => {
    if (!notifyIfMissingRequiredReimbursementFields()) return
    const contractEnd = detail.applicationData?.contract_end_date
    if (
      contractEnd &&
      submitValues.rental_status_date &&
      isReimbursementRequestDateAfterLeaseEnd(
        submitValues.reimbursement_type,
        submitValues.rental_status_date,
        String(contractEnd),
      )
    ) {
      showValidationMessage(REIMBURSEMENT_REQUEST_DATE_AFTER_LEASE_END_MESSAGE)
      return
    }

    // Mirror Angular resident-edit.component.ts:805-832 pre-save guard:
    // When a Liberty role user edits calculation inputs, totalPaid > 0, and totalPaid !== net_due,
    // prompt 'Calculation Adjustments - Not Paid'.
    const dirtyFields = form.formState.dirtyFields
    const isCalcDirty = Boolean(
      dirtyFields.adjusted_notice ||
        dirtyFields.rental_status_date ||
        dirtyFields.loss_adjustment ||
        dirtyFields.first_month_not_paid_year ||
        dirtyFields.first_month_not_paid_month ||
        dirtyFields.unused_security_deposit ||
        dirtyFields.partial_payment_amount ||
        dirtyFields.movein_date,
    )
    const netDue =
      Number(
        String(calculationPaymentPayload?.net_due ?? detail.residentDetail?.resident_default?.net_amount ?? '0').replace(
          /[^0-9.-]/g,
          '',
        ),
      ) || 0
    const totalPaidVal = totalPaid

    if (isLibertyRole && totalPaidVal > 0 && totalPaidVal !== netDue && isCalcDirty) {
      setConfirm({
        title: 'Calculation Adjustments - Not Paid',
        message:
          'You have updated the calculations. However, you have not created a Payment Adjustment for the difference. You should create an Adjustment now?',
        primaryLabel: 'Ok',
        secondaryLabel: 'Cancel',
        onSecondary: async () => {
          setConfirm(null)
          await proceedWithSave(submitValues)
        },
        onPrimary: () => {
          setConfirm(null)
          handleAdjustPayment()
        },
      })
      return
    }

    await proceedWithSave(submitValues)
  })

  const proceedWithSave = useCallback(
    async (submitValues: ResidentDefaultFormValues, moveOutDate?: string) => {
      const original = detail.residentDetail?.resident_default
      const isProgressChange = submitValues.rental_progress !== original?.rental_progress

      // Mirror Angular: confirm before transitioning to EARLY_END and prompt
      // for a move-out date.
      if (submitValues.rental_progress === 'EARLY_END' && isProgressChange) {
        setConfirm({
          title: 'Confirmation',
          message:
            'By updating the status to Closed - Early End, you are confirming that you have either paid out or closed this lease, and will not be filing any default reimbursement on this resident.',
          primaryLabel: 'Confirm',
          secondaryLabel: 'Cancel',
          onSecondary: () => {
            setConfirm(null)
            form.setValue(
              'rental_status',
              detail.residentDetail?.resident_default?.rental_status || '',
              { shouldDirty: false },
            )
            form.setValue(
              'rental_progress',
              detail.residentDetail?.resident_default?.rental_progress || '',
              { shouldDirty: false },
            )
          },
          onPrimary: () => {
            setConfirm(null)
            openDialog('leaseEndDate')
          },
        })
        return
      }

      // Mirror Angular: confirm before transitioning to NOT_MOVED_IN.
      if (submitValues.rental_progress === 'NOT_MOVED_IN' && isProgressChange) {
        setConfirm({
          title: 'Confirmation',
          message:
            'If the resident is marked as "Not Moved In" but does move in, a Reimbursement cannot be filed if they default on rent and the resident will no longer show as an "Active" resident.',
          primaryLabel: 'Continue',
          secondaryLabel: 'Cancel',
          onSecondary: () => {
            setConfirm(null)
            form.setValue(
              'rental_status',
              detail.residentDetail?.resident_default?.rental_status || '',
            )
            form.setValue(
              'rental_progress',
              detail.residentDetail?.resident_default?.rental_progress || '',
            )
          },
          onPrimary: async () => {
            setConfirm(null)
            await persistSave(submitValues)
          },
        })
        return
      }

      await persistSave(submitValues, moveOutDate ?? submitValues.moveout_date)
    },
    [detail.residentDetail?.resident_default, form, openDialog, persistSave],
  )

  /** Matches Angular `tabChange`: switch tabs immediately with no intermediate dialog. */
  const changeTab = (tab: ResidentDetailTab) => {
    setSelectedTab(tab)
  }

  const handleLeaseEndDateCancel = useCallback(() => {
    closeDialog('leaseEndDate')
    form.setValue(
      'rental_status',
      detail.residentDetail?.resident_default?.rental_status || '',
      { shouldDirty: false },
    )
    form.setValue(
      'rental_progress',
      detail.residentDetail?.resident_default?.rental_progress || '',
      { shouldDirty: false },
    )
  }, [closeDialog, detail.residentDetail?.resident_default, form])

  const handleLeaseEndDateSubmit = useCallback(
    async (moveoutDate: string) => {
      closeDialog('leaseEndDate')
      form.setValue('moveout_date', moveoutDate, { shouldDirty: true })
      const submitValues = { ...form.getValues(), moveout_date: moveoutDate }
      await persistSave(submitValues, moveoutDate)
    },
    [closeDialog, form, persistSave],
  )

  const handleAccessConfirmation = useCallback(async () => {
    const applicationId = detail.applicationData?.application_id
    if (!applicationId) return
    setAccessConfirmationLoading(true)
    try {
      const resp = await getPolicyDeclarationFiles(applicationId)
      const files = resp.files ?? []
      if (files.length === 0) {
        showValidationMessage('No files found')
        return
      }
      if (files.length === 1) {
        const streamUrl = files[0]?.stream_url
        if (!streamUrl) {
          showValidationMessage('File URL not available')
          return
        }
        await openServeFileInNewTab(streamUrl)
      } else {
        setPolicyFilesPopup(files)
      }
    } catch (err) {
      showApiError(err)
    } finally {
      setAccessConfirmationLoading(false)
    }
  }, [detail.applicationData?.application_id])

  const approvePayment = (row: PaymentReviewFormRow) => {
    const id = row.review_log_id || row.payment_log_id
    if (!id) return
    void paymentsHook.approvePayment(id, { approved: true }).then(() => showSuccess('Payment approved successfully')).catch(showApiError)
  }

  // Angular `rejectClaim`: capture a REQUIRED reason via ReasonComponent, then POST
  // `/api/claim_review_log/:review_log_id` with `{ is_rejected: '1', review_remark: <note> }`.
  const rejectClaim = (row: PaymentReviewFormRow) => {
    const id = row.review_log_id || row.payment_log_id
    if (!id) return
    setPendingRejectClaimId(id)
    openDialog('rejectReason')
  }

  const confirmRejectClaim = (note: string) => {
    closeDialog('rejectReason')
    const id = pendingRejectClaimId
    setPendingRejectClaimId(null)
    if (!id) return
    void paymentsHook
      .saveReviewLog(id, { is_rejected: '1', review_remark: note })
      .then(() => showSuccess('Claim rejected successfully'))
      .catch(showApiError)
  }

  // Angular `editPayment(paymentLog)`: opens the payment editor (DefaultPaymentComponent) for that
  // row — NOT the settlement-statement generator.
  const handleEditPayment = (row: PaymentReviewFormRow) => {
    setEditPaymentRow(row)
    openDialog('editPayment')
  }

  /**
   * Angular `adjustPayment()`: opens DefaultPaymentComponent with `isAdjustPayment: true`.
   * Initial adjustment = max(0, net_due - previousDefaultPayment).
   */
  const handleAdjustPayment = useCallback(() => {
    if (!defaultId) return
    openDialog('adjustPayment')
  }, [defaultId, openDialog])

  const totalPaid = useMemo(
    () => sumPaymentHistoryTotalPaid(detail.residentDetail?.payment_history),
    [detail.residentDetail?.payment_history],
  )

  const previousDefaultPayment = useMemo(
    () => sumPaymentHistoryPreviousDefaultPayment(detail.residentDetail?.payment_history),
    [detail.residentDetail?.payment_history],
  )

  const canShowAdjustPayment = canShowAdjustPaymentButton(
    role.roleName,
    detail.residentDetail?.payment_history,
  )

  // ---- Approval decision flow (Angular approvedDefaultPayment / approvedDefaultClaim) ----
  const revertApproval = () => {
    setPendingApproval(null)
    setApprovalReviewClaim(null)
    // Remount the review-log dropdowns so a cancelled decision snaps back to its saved value.
    setApprovalResetKey((k) => k + 1)
  }

  // Angular `savePayment` / `saveClaim`: always show the "Update Status" confirm before the POST.
  const askUpdateStatusConfirm = (
    pending: { kind: 'payment' | 'claim'; id: string | number; field: ReviewApprovalField; value: ReviewApprovalValue },
    note?: string,
  ) => {
    setConfirm({
      title: 'Update Status',
      message: 'Please confirm, that you really want to update status?',
      primaryLabel: 'Yes',
      secondaryLabel: 'No',
      onPrimary: () => {
        setConfirm(null)
        setPendingApproval(null)
        const submit =
          pending.kind === 'payment'
            ? paymentsHook.submitPaymentApproval(pending.id, pending.field, pending.value, note)
            : paymentsHook.submitClaimReviewApproval(pending.id, pending.field, pending.value, note)
        void Promise.resolve(submit)
          .then(() => showSuccess('Data Updated Successfully'))
          .catch(showApiError)
      },
      onSecondary: () => {
        setConfirm(null)
        revertApproval()
      },
    })
  }

  // Angular `approvedDefaultPayment`: every payment decision captures a reason, then confirms.
  const handlePaymentApproval = (
    id: string | number,
    field: ReviewApprovalField,
    value: ReviewApprovalValue,
  ) => {
    setPendingApproval({ kind: 'payment', id, field, value })
    openDialog('approvalReason')
  }

  // Angular `approvedDefaultClaim`: return/escalate/recalc (0/2/3/4) and L1-recalc capture a reason;
  // a non-L1 "Approve" (value 1) opens the Review Default Reimbursement modal; else confirm-only.
  const handleClaimApproval = (
    reviewLogId: string | number,
    field: ReviewApprovalField,
    value: ReviewApprovalValue,
  ) => {
    if (['0', '2', '3', '4'].includes(String(value)) || (value === '1' && field === 'l1_approved')) {
      setPendingApproval({ kind: 'claim', id: reviewLogId, field, value })
      openDialog('approvalReason')
    } else if (value === '1' && field !== 'l1_approved') {
      const targetRow = (detail.residentDetail?.payment_review_log || []).find(
        (r) => String(r.review_log_id) === String(reviewLogId),
      )
      setApprovalReviewClaim({
        reviewLogId,
        approvalType: field,
        approvalTypeValue: String(value),
        defaultType: targetRow?.claim_status || null,
      })
      openDialog('reviewClaim')
    } else {
      askUpdateStatusConfirm({ kind: 'claim', id: reviewLogId, field, value })
    }
  }

  const confirmApprovalReason = (note: string) => {
    closeDialog('approvalReason')
    if (pendingApproval) askUpdateStatusConfirm(pendingApproval, note)
  }
  const cancelApprovalReason = () => {
    closeDialog('approvalReason')
    revertApproval()
  }

  // Angular `covidchange()`: selecting "Yes" opens the COVID reason dialog; "No" just sets the flag.
  const handleCovidChange = (value: string) => {
    form.setValue('covid_19', value, { shouldDirty: true })
    if (value === '1') {
      openDialog('covidReason')
    }
  }

  /** Angular `setCovidValue()` — revert to the persisted `special_note`-derived value on cancel. */
  const covidRevertValue = (): string =>
    detail.residentDetail?.resident_default?.special_note === 'COVID_19_YES' ? '1' : '0'

  // Angular covidchange refresh: POST `/api/default/:uid` with `{ notes: 'COVID - <note>', page:
  // 'default', covid_19: '1', application_id }` then reload notes + detail.
  const confirmCovidReason = (note: string) => {
    closeDialog('covidReason')
    const body: Record<string, unknown> = {
      notes: `COVID - ${note}`,
      page: 'default',
      covid_19: '1',
    }
    if (detail.applicationData?.application_id) {
      body.application_id = detail.applicationData.application_id
    }
    void detail
      .save(body)
      .then(async () => {
        form.setValue('message', '', { shouldDirty: false })
        await detail.load({ silent: true })
        await reloadPublicNotes()
      })
      .catch(showApiError)
  }

  const cancelCovidReason = () => {
    closeDialog('covidReason')
    form.setValue('covid_19', covidRevertValue(), { shouldDirty: true })
  }

  /**
   * Angular `firstMonthValidation()`: the First Month Rent Not Paid must fall within the lease.
   * If it is before lease start / after lease end: non-Liberty roles get the field cleared + a
   * notify; Liberty roles get an override confirm that sets `claim_override` (sent as `'1'`).
   */
  const handleFirstMonthChange = () => {
    // Defer so react-hook-form has committed the newly selected month/year.
    queueMicrotask(() => {
      const v = form.getValues()
      const month = String(v.first_month_not_paid_month || '').trim()
      const year = String(v.first_month_not_paid_year || '').trim()
      if (!v.rental_status_date || !month || !year) return

      const parseYmd = (s?: string): Date | null => {
        if (!s) return null
        const iso = /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(0, 10)}T12:00:00` : s
        const d = new Date(iso)
        return Number.isNaN(d.getTime()) ? null : d
      }
      const firstMonth = new Date(`${year}-${month.padStart(2, '0')}-01T12:00:00`)
      if (Number.isNaN(firstMonth.getTime())) return
      const leaseStart = parseYmd(v.lease_start_date)
      const leaseEnd = parseYmd(v.lease_end_date)

      const clearMonth = () =>
        form.setValue('first_month_not_paid_month', '', { shouldDirty: true })
      const openOverrideConfirm = () =>
        setConfirm({
          title: 'First Month Rent Not Paid Override',
          message:
            'The First Month Rent Not Paid cannot be prior to the start of the lease. Is an override required for the review of this reimbursement?',
          primaryLabel: 'Yes',
          secondaryLabel: 'No',
          onPrimary: () => {
            setConfirm(null)
            claimOverrideRef.current = true
          },
          onSecondary: () => {
            setConfirm(null)
            clearMonth()
          },
        })

      if (leaseStart && firstMonth < leaseStart) {
        if (!isLibertyRole) {
          clearMonth()
          showValidationMessage('First Month Rent Not Paid cannot be prior to the Lease')
        } else {
          openOverrideConfirm()
        }
      } else if (leaseEnd && firstMonth > leaseEnd) {
        if (!isLibertyRole) {
          clearMonth()
          showValidationMessage('First Month Rent Not Paid cannot be after the Lease')
        } else {
          openOverrideConfirm()
        }
      }
    })
  }

  const showL1ReviewRecommendation = useCallback(() => {
    setConfirm({
      title: L1_REVIEW_RECOMMENDATION_TITLE,
      message: L1_REVIEW_RECOMMENDATION_MESSAGE,
      primaryLabel: 'Ok',
      secondaryLabel: '',
      messageAlign: 'left',
      messageWeight: 'normal',
      primaryButtonClassName: primaryEntityActionButtonClass,
      onPrimary: () => setConfirm(null),
    })
  }, [])

  /** Mirror Angular `claimDenyReopen` / `l1_review_needed` — requires `resident_default.l1_review`. */
  const requireL1ReviewThen = (action: () => void) => {
    if (!detail.residentDetail?.resident_default?.l1_review) {
      showL1ReviewRecommendation()
      return
    }
    action()
  }

  const calculationPaymentPayload = useMemo(() => {
    // Angular `amountData: defaultProcessData` after adjustPayment / showPaymentPopup date merge.
    const snapshot = getCalculationTabSnapshot(
      detail.residentDetail,
      detail.applicationData,
      values,
    )
    return buildAdjustmentDataFromForm({
      snapshot,
      formValues: values,
      rentAmount: detail.applicationData?.rent_amount ?? snapshot.rent_amount,
    })
  }, [detail.applicationData, detail.residentDetail, values])

  const adjustPaymentInitialAmount = useMemo(() => {
    const netDue = calculationPaymentPayload?.net_due
    const net =
      Number(
        String(netDue ?? detail.residentDetail?.resident_default?.net_amount ?? '0').replace(
          /[^0-9.-]/g,
          '',
        ),
      ) || 0
    const previous = previousDefaultPayment
    const adjustment = previous < net ? net - previous : 0
    return adjustment.toFixed(2)
  }, [
    calculationPaymentPayload?.net_due,
    detail.residentDetail?.resident_default?.net_amount,
    previousDefaultPayment,
  ])

  const editPaymentInitialAmount = useMemo(() => {
    if (!editPaymentRow) return '0.00'
    const netDue =
      calculationPaymentPayload?.net_due ?? detail.residentDetail?.resident_default?.net_amount
    const amt = deriveEditPaymentInitialAmount(
      editPaymentRow,
      detail.residentDetail?.payment_history,
      netDue as string | number | null | undefined,
    )
    return amt.toFixed(2)
  }, [
    editPaymentRow,
    calculationPaymentPayload?.net_due,
    detail.residentDetail?.resident_default?.net_amount,
    detail.residentDetail?.payment_history,
  ])

  const propertyId =
    detail.applicationData?.property?.property_id ?? detail.applicationData?.fk_property_id

  /** Legacy claim-level mark received — not per-document 3.0 review (`reviewReimbursementDocument`). */
  const runMarkDocumentsReceivedAndOpenPayment = useCallback(
    async (emailSend: boolean) => {
      if (!defaultId) return
      try {
        await reviewResidentStatus(defaultId, {
          notification_date: true,
          notes: '',
          lease_agreement: true,
          final_payment_ledger: true,
          legal_forms: true,
          status: 'received',
          ...(emailSend ? { email_send: true } : {}),
          payment_flag: true,
        })
        showSuccess('Default Reimbursement Updated Successfully!')
        await detail.load({ silent: true })
        openDialog('configurePayment')
      } catch (err) {
        showApiError(err)
      }
    },
    [defaultId, detail, openDialog],
  )

  /** Mirror Angular `defaultPayment()` — L1 gate, document status, then payment popup. */
  const handleConfigurePayment = useCallback(() => {
    if (!defaultId) return
    if (!notifyIfMissingRequiredReimbursementFields()) return

    const rd = detail.residentDetail?.resident_default
    if (!rd?.l1_review) {
      showL1ReviewRecommendation()
      return
    }

    if (rd.document_status === 'RECEIVED') {
      openDialog('configurePayment')
      return
    }

    setConfirm({
      title: 'Documents Status',
      message:
        "Documents have not been received, but will be marked as 'Received' if you continue. Do you want to proceed?",
      primaryLabel: 'Yes',
      secondaryLabel: 'No',
      primaryFirst: true,
      onPrimary: () => {
        setConfirm(null)
        setConfirm({
          title: 'Send to Property?',
          message: 'Do you want to send a notice to the Property?',
          primaryLabel: 'Yes',
          secondaryLabel: 'No',
          primaryFirst: true,
          onPrimary: () => {
            setConfirm(null)
            void runMarkDocumentsReceivedAndOpenPayment(true)
          },
          onSecondary: () => {
            setConfirm(null)
            void runMarkDocumentsReceivedAndOpenPayment(false)
          },
        })
      },
      onSecondary: () => setConfirm(null),
    })
  }, [
    defaultId,
    detail.residentDetail?.resident_default,
    notifyIfMissingRequiredReimbursementFields,
    openDialog,
    runMarkDocumentsReceivedAndOpenPayment,
    showL1ReviewRecommendation,
  ])

  /** Angular `reviewClaim()` → Review Default Reimbursement (`ReviewClaimComponent`). */
  const handleOpenReviewReimbursement = () => {
    if (!notifyIfMissingRequiredReimbursementFields()) return
    openDialog('reviewClaim')
  }

  // Angular `getCalculationJSON()`: full `defaultProcessData` snapshot + rent_amount /
  // first_month_not_paid / timely / adjust_max_movein_date (+ form date merge).
  // Sent as `adjustment_data` on deny/reopen (and `net_amount` = net_due on deny).
  const buildDenyReopenExtras = (includeNetAmount: boolean): Record<string, unknown> => {
    const v = form.getValues()
    const snapshot = getCalculationTabSnapshot(detail.residentDetail, detail.applicationData, v)
    const adjustmentData = buildAdjustmentDataFromForm({
      snapshot,
      formValues: v,
      rentAmount: detail.applicationData?.rent_amount ?? snapshot.rent_amount,
    })
    const extras: Record<string, unknown> = { adjustment_data: adjustmentData }
    if (includeNetAmount) extras.net_amount = snapshot.net_due
    return extras
  }

  const handleDenyClaim = () => {
    if (!defaultId) return
    if (!notifyIfMissingRequiredReimbursementFields()) return
    setConfirm({
      title: 'Confirmation',
      message: 'Do you want to Deny this Reimbursement?',
      primaryLabel: 'Deny',
      secondaryLabel: 'Cancel',
      onPrimary: async () => {
        setConfirm(null)
        try {
          await claimDenyReopen(defaultId, { type: 'claim_denied', ...buildDenyReopenExtras(true) })
          showSuccess('Reimbursement Action Updated Successfully')
          await detail.load({ silent: true })
        } catch (err) {
          showApiError(err)
        }
      },
    })
  }

  const handleReopenClaim = () => {
    if (!defaultId) return
    if (!notifyIfMissingRequiredReimbursementFields()) return
    setConfirm({
      title: 'Confirmation',
      message: 'Do you want to Reopen this Reimbursement?',
      primaryLabel: 'Reopen',
      secondaryLabel: 'Cancel',
      onPrimary: async () => {
        setConfirm(null)
        try {
          await claimDenyReopen(defaultId, { type: 'claim_reopened', ...buildDenyReopenExtras(false) })
          showSuccess('Reimbursement Action Updated Successfully')
          await detail.load({ silent: true })
        } catch (err) {
          showApiError(err)
        }
      },
    })
  }

  const handleCancelReimbursementSuccess = useCallback(() => {
    if (!applicantId || !userId) return
    const formattedApplicantId = formatApplicantIdSegment(applicantId)
    const targetUrl = `/resident/resident-detail/${formattedApplicantId}/${userId}`
    window.location.href = targetUrl
  }, [applicantId, userId])

  const handleCancelReimbursement = () => {
    if (!defaultId) return
    openDialog('cancelReimbursementRequest')
  }

  const downloadFileRow = (row: {
    file_path?: string
    filepath?: string
    filename?: string
    attachment_path?: string
    attachment_id?: string | number
  }) => {
    const filepath = row.filepath || row.file_path || row.attachment_path
    if (filepath && row.attachment_id) {
      void openResidentClaimsDocument('LEASE_AGREEMENT', row.attachment_id, filepath).catch(showApiError)
      return
    }
    if (filepath) openApiPath(String(filepath).startsWith('/') ? String(filepath) : `/${String(filepath)}`)
  }

  const downloadLibertyDocument = (
    row: {
      id?: string | number
      attachment_id?: string | number
      attachment_path?: string
      attachment_name?: string
    },
    siblingDocs?: Array<{
      id?: string | number
      attachment_id?: string | number
      attachment_path?: string
      attachment_name?: string
    }>,
  ) => {
    const fileId = row.id ?? row.attachment_id
    if (fileId && row.attachment_path) {
      const siblings = siblingDocs ?? documentsHook.libertyDocuments ?? []
      const filesList = buildLibertyDocumentImagePreviewList(siblings)
      return openResidentClaimsPreview(
        'LIBERTY_DOCUMENT',
        fileId,
        row.attachment_path,
        row.attachment_name || '',
        filesList,
      ).catch(showApiError)
    }
    if (row.attachment_path) {
      const p = String(row.attachment_path)
      openApiPath(p.startsWith('/') ? p : `/${p}`)
    }
    return Promise.resolve()
  }

  /** Liberty Document force-download (blob save), separate from view/open. */
  const saveLibertyDocument = (row: {
    id?: string | number
    attachment_id?: string | number
    attachment_path?: string
    attachment_name?: string
  }) => {
    const fileId = row.id ?? row.attachment_id
    if (fileId && row.attachment_path) {
      return downloadResidentClaimsPreview(
        'LIBERTY_DOCUMENT',
        fileId,
        row.attachment_path,
        row.attachment_name || '',
      ).catch(showApiError)
    }
    if (row.attachment_path) {
      const p = String(row.attachment_path)
      openApiPath(p.startsWith('/') ? p : `/${p}`)
    }
    return Promise.resolve()
  }

  const downloadResidentDocument = (
    fileTypeKey: string,
    row: { attachment_id?: string | number; filepath?: string; filename?: string },
  ) => {
    if (row.attachment_id && row.filepath) {
      return openResidentClaimsDocument(fileTypeKey, row.attachment_id, row.filepath).catch(
        showApiError,
      )
    }
    if (row.filepath) openApiPath(row.filepath.startsWith('/') ? row.filepath : `/${row.filepath}`)
    return Promise.resolve()
  }

  /** Settlement statement force-download (blob save). */
  const saveResidentDocument = (
    fileTypeKey: string,
    row: { attachment_id?: string | number; filepath?: string; filename?: string },
  ) => {
    if (row.attachment_id && row.filepath) {
      return downloadResidentClaimsDocument(
        fileTypeKey,
        row.attachment_id,
        row.filepath,
        row.filename || '',
      ).catch(showApiError)
    }
    if (row.filepath) openApiPath(row.filepath.startsWith('/') ? row.filepath : `/${row.filepath}`)
    return Promise.resolve()
  }

  /** Settlement PDF generation — matches Angular `defaultPaymentStatement` body shape. */
  const runGenerateSettlementStatement = useCallback(async () => {
    if (!defaultId) return
    try {
      const submitValues = form.getValues()
      const leaseChanged = isLeaseChanged(submitValues, detail.residentDetail?.resident_default)
      const payload = buildSettlementGeneratePayload({
        values: submitValues,
        residentDetail: detail.residentDetail,
        applicationData: detail.applicationData,
        applicationId: detail.applicationData?.application_id,
        roleName: role.roleName,
        leaseChanged,
        moveOutDate: submitValues.moveout_date || undefined,
        rentalProgressChangedToEarlyEnd: false,
      })
      await generateSettlementStatement(defaultId, payload)
      showSuccess('Default Settlement Statement Generated Successfully')
      await detail.load({ silent: true })
    } catch (err) {
      showApiError(err)
    }
  }, [defaultId, detail, form, role.roleName])

  const openGenerateSettlementConfirm = useCallback(() => {
    setConfirm({
      title: 'Default Settlement Statement',
      message:
        'Generate the settlement statement using the current reimbursement fields and calculation figures?',
      primaryLabel: 'Generate',
      secondaryLabel: 'Cancel',
      onPrimary: async () => {
        setConfirm(null)
        await runGenerateSettlementStatement()
      },
    })
  }, [runGenerateSettlementStatement])

  /**
   * Angular `selectFile` → `uploadFile` → `submitForm('fileUpload')`.
   * Posts the file immediately; does not require the form Save button.
   */
  const uploadClaimDocumentImmediate = useCallback(
    async (fieldName: ResidentImmediateUploadField, files: File[]) => {
      if (!files.length) return
      setDocumentUploadingField(fieldName)
      try {
        const claimLock =
          String(
            (detail.residentDetail?.resident_default as Record<string, unknown> | undefined)
              ?.claim_lock ?? '',
          ).trim() === 'Locked'
        const body = buildResidentImmediateFileUploadFormData({
          fieldName,
          files,
          applicationId: detail.applicationData?.application_id ?? applicationId,
          claimLock,
          formValues: form.getValues(),
          dirtyFields: form.formState.dirtyFields,
          page: apiPageForDetailTab(selectedTab),
        })
        if (apiPageForDetailTab(selectedTab) === 'default') {
          appendReimbursement3ScalarFieldsToFormData(
            body,
            reimbursementExtendedFieldsRef.current,
          )
          appendUnusedSecurityDepositToFormData(
            body,
            form.getValues(),
            reimbursementExtendedFieldsRef.current.security_deposit_collected,
          )
        }
        await detail.save(body)
        setLedgerUploadFinalIntent(null)
        form.setValue(fieldName as keyof ResidentDefaultFormValues, '' as never, {
          shouldDirty: false,
        })
        showSuccess('File Uploaded Successfully')
      } catch (err) {
        showApiError(err)
        throw err
      } finally {
        setDocumentUploadingField(null)
      }
    },
    [applicationId, detail, form, selectedTab],
  )

  const registerReimbursementExtendedFields = useCallback((fields: ReimbursementExtendedFields) => {
    reimbursementExtendedFieldsRef.current = fields
  }, [])

  const submitReimbursementSectionByMilestone = useCallback(
    async (milestoneId: ReimbursementMilestoneId) => {
      if (!userId || !applicationId) return
      const values = form.getValues()
      const payload = buildReimbursementUpdatePayload(
        values as unknown as ReimbursementsDetailValues,
        applicationId,
        isLibertyRole,
      )
      appendReimbursement3ScalarFields(payload, reimbursementExtendedFieldsRef.current)
      applyUnusedSecurityDepositToApiPayload(
        payload,
        values,
        reimbursementExtendedFieldsRef.current.security_deposit_collected,
        'default-detail',
      )
      try {
        await submitReimbursementSection(
          userId,
          applicationId,
          MILESTONE_TO_SECTION[milestoneId],
          payload,
        )
        showSuccess('Section submitted successfully')
        await detail.load({ silent: true })
      } catch (err) {
        showApiError(err)
        throw err
      }
    },
    [applicationId, detail, form, isLibertyRole, userId],
  )

  const submitReimbursementForLibertyReviewAction = useCallback(async () => {
    if (!isLibertyRole) return
    if (!userId || !applicationId) return
    if (!notifyIfMissingRequiredReimbursementFields()) return
    const values = form.getValues()
    const payload = buildReimbursementUpdatePayload(
      values as unknown as ReimbursementsDetailValues,
      applicationId,
      isLibertyRole,
    )
    appendReimbursement3ScalarFields(payload, reimbursementExtendedFieldsRef.current)
    applyUnusedSecurityDepositToApiPayload(
      payload,
      values,
      reimbursementExtendedFieldsRef.current.security_deposit_collected,
      'default-detail',
    )
    try {
      await submitReimbursementForLibertyReview(userId, applicationId, payload)
      showSuccess('Submitted for review')
      await detail.load({ silent: true })
    } catch (err) {
      showApiError(err)
      throw err
    }
  }, [applicationId, detail, form, isLibertyRole, notifyIfMissingRequiredReimbursementFields, userId])

  const unlockReimbursementSectionByMilestone = useCallback(
    async (milestoneId: ReimbursementMilestoneId) => {
      if (!isLibertyRole) return
      if (!userId || !applicationId) return
      try {
        await unlockReimbursementSection(userId, applicationId, MILESTONE_TO_SECTION[milestoneId])
        showSuccess('Section unlocked')
        await detail.load({ silent: true })
      } catch (err) {
        showApiError(err)
        throw err
      }
    },
    [applicationId, detail, isLibertyRole, userId],
  )

  const reviewReimbursementDocument = useCallback(
    async (
      docType: ReimbursementDocumentType,
      decision: 'approved' | 'incomplete',
      options?: { reason?: string; attachmentId?: string | number; email_send?: boolean },
    ) => {
      if (!isLibertyRole) return
      if (!defaultId) return
      const payload = buildDocumentReviewPayload(
        docType,
        decision === 'approved' ? 'APPROVED' : 'INCOMPLETE',
        {
          reason: options?.reason,
          email_send: options?.email_send ?? false,
          reimbursement_attachment_id: options?.attachmentId,
        },
      )
      if (!payload) {
        showValidationMessage('This document type cannot be reviewed yet.')
        return
      }
      try {
        await reviewResidentDocuments(defaultId, payload)
        showSuccess('Document review saved.')
        await detail.load({ silent: true })
      } catch (err) {
        showApiError(err)
        throw err
      } finally {
        setDocumentUploadingField(null)
      }
    },
    [defaultId, detail, isLibertyRole],
  )

  const reclassifyReimbursementDocument = useCallback(
    async (
      fromDocType: ReimbursementDocumentType,
      toDocType: ReimbursementDocumentType,
      options?: { attachmentId?: string | number },
    ) => {
      if (!isLibertyRole) return
      if (!defaultId) return
      const payload = buildDocumentReclassifyPayload(fromDocType, toDocType, {
        reimbursement_attachment_id: options?.attachmentId,
      })
      if (!payload) {
        showValidationMessage('This document cannot be reclassified to the selected type.')
        return
      }
      try {
        await reviewResidentDocuments(defaultId, payload)
        showSuccess('Document reclassified.')
        await detail.load({ silent: true })
      } catch (err) {
        showApiError(err)
        throw err
      } finally {
        setDocumentUploadingField(null)
      }
    },
    [defaultId, detail, isLibertyRole],
  )

  const uploadSettlementStatementFiles = useCallback(
    async (files: File[]) => {
      if (pageType !== 'default-detail') return
      const uploadId = resolveOldStatementUploadId(
        detail.residentDetail as Record<string, unknown> | null,
      )
      if (!uploadId || !files.length) return
      const values = form.getValues()
      const reimbursementRental = resolveReimbursementRentalFieldsForApi(values)
      const rentalStatus = reimbursementRental.rental_status ?? ''
      setStatementUploading(true)
      try {
        await uploadOldStatement(uploadId, files, {
          page: getDefaultApiPage({
            selectedTab,
            pageType,
            rentalStatus,
          }),
          ...(rentalStatus ? { rental_status: rentalStatus } : {}),
        })
        showSuccess('Documents Added Successfully')
        await detail.load({ silent: true })
      } catch (err) {
        showApiError(err)
      } finally {
        setStatementUploading(false)
      }
    },
    [detail, form, pageType, selectedTab],
  )

  const libertyDocumentsBusy = Boolean(
    documentsHook.processingKey &&
      (documentsHook.processingKey === 'liberty-upload' ||
        documentsHook.processingKey.startsWith('liberty-')),
  )

  const showSaveBar = shouldShowSaveBar(selectedTab) && selectedTab !== 'residentinfo'
  /**
   * Calculation tab uses inline Save after calculation fields (Angular `resident-edit-new`), not a footer bar.
   * Reimbursement uses toolbar Save + actions; residentinfo uses inline Save in the section.
   */
  const showFooterSaveBar =
    showSaveBar && selectedTab !== 'default' && selectedTab !== 'calculation'
  const isReimbursementStatus = isReimbursementClaimType(values.reimbursement_type)

  return {
    applicantId,
    userId,
    pageType,
    role,
    detail,
    selectedTab,
    setSelectedTab,
    changeTab,
    visibleTabs,
    showRequired,
    missingFields,
    form,
    values,
    formDisabled,
    markEndDateOverridePreserved,
    isLibertyRole,
    isCompanyRole,
    isPropertyRole,
    isExternalInsurance,
    currentUserDisplayName,
    loggedUserId,
    defaultId,
    applicationId,
    notesHook,
    publicNotesMergedRows,
    publicNotesLoading,
    reloadPublicNotes,
    publicMessageSaving,
    internalNoteSaving,
    internalNotesDraft,
    setInternalNotesDraft,
    documentsHook,
    paymentsHook,
    confirm,
    setConfirm,
    dialogs,
    openDialog,
    closeDialog,
    handleLeaseEndDateCancel,
    handleLeaseEndDateSubmit,
    policyFilesPopup,
    setPolicyFilesPopup,
    accessConfirmationLoading,
    showEorpModal,
    setShowEorpModal,
    ledgerUploadFinalIntent,
    setLedgerUploadFinalIntent,
    documentUploadingField,
    uploadClaimDocumentImmediate,
    reviewReimbursementDocument,
    reclassifyReimbursementDocument,
    registerReimbursementExtendedFields,
    submitReimbursementSectionByMilestone,
    submitReimbursementForLibertyReviewAction,
    unlockReimbursementSectionByMilestone,
    documentReviewIncompleteReasons:
      detail.statusList?.document_review_incomplete_reasons ?? [],
    pendingDocumentRemove,
    setPendingDocumentRemove,
    publicNotesNotifyOpen,
    setPublicNotesNotifyOpen,
    save,
    showSaveBar,
    showFooterSaveBar,
    isReimbursementStatus,
    submitPublicNotesMessage,
    submitInternalNote,
    handleSavePublicMessage,
    handleAccessConfirmation,
    handleRentalStatusDateUserChange,
    handleRentalStatusUserChange,
    handleConfigurePayment,
    handleAdjustPayment,
    canShowAdjustPayment,
    totalPaid,
    previousDefaultPayment,
    adjustPaymentInitialAmount,
    editPaymentInitialAmount,
    handleOpenReviewReimbursement,
    handleDenyClaim,
    handleReopenClaim,
    handleCancelReimbursement,
    requireL1ReviewThen,
    approvePayment,
    rejectClaim,
    confirmRejectClaim,
    handleEditPayment,
    editPaymentRow,
    handlePaymentApproval,
    handleClaimApproval,
    confirmApprovalReason,
    cancelApprovalReason,
    approvalResetKey,
    approvalReviewClaim,
    revertApproval,
    handleCovidChange,
    confirmCovidReason,
    cancelCovidReason,
    handleFirstMonthChange,
    downloadFileRow,
    downloadResidentDocument,
    saveResidentDocument,
    downloadLibertyDocument,
    saveLibertyDocument,
    statementUploading,
    uploadSettlementStatementFiles,
    openGenerateSettlementConfirm,
    libertyDocumentsBusy,
    calculationPaymentPayload,
    isOverrideCleared,
    propertyId,
    reloadDetail,
    handleCancelReimbursementSuccess,
  }
}
